<template>
  <div class="hero-sky" :class="{ 'is-still': still, 'is-entering': entering && !still }" aria-hidden="true">
    <!-- Shared SVG definitions: cloud shapes, their gradients and blurs, and
         the aurora's gradients, mask and clips. A 0x0 (not display:none) svg,
         so gradients defined in it keep working in every browser. -->
    <svg class="sky-defs" width="0" height="0" focusable="false">
      <defs>
        <!-- Day cumulus: one vertical gradient for the whole cloud (white
             tops, blue-grey underside), so overlapping lobes fuse without
             seams, plus soft radial highlights that round the front lobes. -->
        <linearGradient :id="sid('cgd')" x1="0" y1="8" x2="0" y2="92" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#ffffff"/>
          <stop offset="0.4" stop-color="#fbfdff"/>
          <stop offset="0.68" stop-color="#e4ecf6"/>
          <stop offset="0.87" stop-color="#c9d7e8"/>
          <stop offset="1" stop-color="#b6c7dd"/>
        </linearGradient>
        <radialGradient :id="sid('chd')" cx="0.56" cy="0.4" r="0.56">
          <stop offset="0" stop-color="#ffffff" stop-opacity="1"/>
          <stop offset="0.6" stop-color="#ffffff" stop-opacity="0.72"/>
          <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
        <linearGradient :id="sid('cgc')" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0"/>
          <stop offset="0.3" stop-color="#ffffff" stop-opacity="0.85"/>
          <stop offset="0.72" stop-color="#ffffff" stop-opacity="0.8"/>
          <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
        </linearGradient>
        <!-- Night veils: translucent, lit from above (the moon), thinning to
             nothing at the base; the rims catch the moonlight on the right. -->
        <linearGradient :id="sid('cgn')" x1="0" y1="36" x2="0" y2="80" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#9fb0d6" stop-opacity="0.5"/>
          <stop offset="0.4" stop-color="#5d6f96" stop-opacity="0.36"/>
          <stop offset="1" stop-color="#2c3a5c" stop-opacity="0"/>
        </linearGradient>
        <linearGradient :id="sid('crn')" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#c9d6f2" stop-opacity="0"/>
          <stop offset="0.55" stop-color="#d4e0f8" stop-opacity="0.42"/>
          <stop offset="1" stop-color="#e4ecff" stop-opacity="0.58"/>
        </linearGradient>
        <linearGradient :id="sid('cgcn')" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#aebfe4" stop-opacity="0"/>
          <stop offset="0.35" stop-color="#b4c4e8" stop-opacity="0.5"/>
          <stop offset="0.72" stop-color="#bccbec" stop-opacity="0.46"/>
          <stop offset="1" stop-color="#bccbec" stop-opacity="0"/>
        </linearGradient>
        <!-- Softness per depth: far clouds are blurrier (atmospheric depth). -->
        <filter v-for="f in cloudFilters" :id="sid(f.id)" :key="f.id" x="-20%" y="-60%" width="140%" height="220%">
          <feGaussianBlur :stdDeviation="f.blur"/>
        </filter>

        <g v-for="(shape, i) in dayClouds" :id="sid('cd' + i)" :key="'cd' + i">
          <template v-if="shape.streaks">
            <ellipse v-for="(e, j) in shape.streaks" :key="j" :cx="e[0]" :cy="e[1]" :rx="e[2]" :ry="e[3]"
                     :transform="rot(e)" :fill="surl('cgc')"/>
          </template>
          <template v-else>
            <g :fill="surl('cgd')">
              <ellipse v-for="(e, j) in shape.base" :key="'b' + j" :cx="e[0]" :cy="e[1]" :rx="e[2]" :ry="e[3]"/>
              <circle v-for="(c, j) in shape.lobes" :key="'l' + j" :cx="c[0]" :cy="c[1]" :r="c[2]"/>
            </g>
            <circle v-for="(c, j) in shape.caps" :key="'c' + j" :cx="c[0]" :cy="c[1]" :r="c[2]" :fill="surl('chd')"/>
          </template>
        </g>
        <g v-for="(shape, i) in nightClouds" :id="sid('cn' + i)" :key="'cn' + i">
          <template v-if="shape.streaks">
            <ellipse v-for="(e, j) in shape.streaks" :key="j" :cx="e[0]" :cy="e[1]" :rx="e[2]" :ry="e[3]"
                     :transform="rot(e)" :fill="surl('cgcn')"/>
          </template>
          <template v-else>
            <ellipse v-for="(e, j) in shape.body" :key="'b' + j" :cx="e[0]" :cy="e[1]" :rx="e[2]" :ry="e[3]"
                     :transform="rot(e)" :fill="surl('cgn')"/>
            <ellipse v-for="(e, j) in shape.rims" :key="'r' + j" :cx="e[0]" :cy="e[1]" :rx="e[2]" :ry="e[3]"
                     :transform="rot(e)" :fill="surl('crn')"/>
          </template>
        </g>

        <linearGradient v-for="(color, i) in auroraColors" :id="sid('av' + i)" :key="'av' + i"
                        x1="0" y1="330" x2="0" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0" :stop-color="color" stop-opacity="0.5"/>
          <stop offset="0.3" :stop-color="color" stop-opacity="0.22"/>
          <stop offset="0.65" :stop-color="color" stop-opacity="0.05"/>
          <stop offset="1" :stop-color="color" stop-opacity="0"/>
        </linearGradient>
        <!-- The rays (vertical light curtains) carry most of the brightness. -->
        <linearGradient v-for="(color, i) in auroraColors" :id="sid('ay' + i)" :key="'ay' + i"
                        x1="0" y1="330" x2="0" y2="20" gradientUnits="userSpaceOnUse">
          <stop offset="0" :stop-color="color" stop-opacity="1"/>
          <stop offset="0.32" :stop-color="color" stop-opacity="0.6"/>
          <stop offset="0.7" :stop-color="color" stop-opacity="0.14"/>
          <stop offset="1" :stop-color="color" stop-opacity="0"/>
        </linearGradient>
        <linearGradient :id="sid('amg')" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#000"/>
          <stop offset="0.24" stop-color="#fff"/>
          <stop offset="0.76" stop-color="#fff"/>
          <stop offset="1" stop-color="#000"/>
        </linearGradient>
        <mask :id="sid('am')" maskUnits="userSpaceOnUse" x="0" y="0" width="1200" height="400">
          <rect width="1200" height="400" :fill="surl('amg')"/>
        </mask>
        <clipPath v-for="(curtain, i) in curtains" :id="sid('ac' + i)" :key="'ac' + i">
          <path :d="curtain.shape"/>
        </clipPath>
      </defs>
    </svg>

    <!-- Day and night skies, stacked: gradients can't transition, so the
         night layer fades over the day one. Between them sit the twilight
         skies (dusk and dawn), which only show WHILE the theme changes. -->
    <div class="sky-fill sky-fill--day"></div>
    <div class="sky-tw sky-tw--dusk"><div class="sky-tw-in"><div class="sky-tw-fill sky-tw-fill--dusk"></div></div></div>
    <div class="sky-tw sky-tw--dawn"><div class="sky-tw-in"><div class="sky-tw-fill sky-tw-fill--dawn"></div></div></div>
    <div class="sky-fill sky-fill--night"></div>
    <div class="sky-grain"></div>
    <div class="sky-haze sky-haze--day"></div>
    <div class="sky-haze sky-haze--night"></div>

    <!-- Horizon afterglow at sunset / sunrise: also only visible while the
         theme changes (outer x inner opacity, each on its own schedule). -->
    <div class="sky-dusk"><div class="sky-dusk-glow"></div></div>

    <!-- Entrance veil: night "lights up", day clears from a dawn tint. -->
    <div class="sky-wake"></div>

    <!-- Far layer (parallax 4px / 3px): aurora, stars, shooting star, sun, moon. -->
    <div class="sky-far">
      <div class="sky-aurora">
        <div v-for="(r, i) in ribbons" :key="'ar' + i" class="aurora-ribbon" :style="r.style">
          <div class="aurora-wave" :class="'aurora-wave--' + r.wave" :style="r.waveStyle">
            <div class="aurora-breathe" :style="r.breatheStyle">
              <svg class="aurora-svg" viewBox="0 0 1200 400" preserveAspectRatio="none" focusable="false">
                <g :mask="surl('am')">
                  <path :d="curtains[r.curtain].shape" :fill="surl('av' + r.color)"/>
                  <g :clip-path="surl('ac' + r.curtain)">
                    <rect v-for="(ray, j) in r.rays" :key="j" :x="ray.x" y="0" :width="ray.w" height="400"
                          :fill="surl('ay' + r.color)" :opacity="ray.o"/>
                  </g>
                  <path :d="curtains[r.curtain].edge" fill="none" :stroke="auroraColors[r.color]"
                        stroke-width="14" stroke-linecap="round" opacity="0.55"/>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div class="sky-stars">
        <!-- Depth behind the twinkling stars: a faint Milky Way band and a
             dust of tiny static stars (one node each, never animated). -->
        <div class="sky-deep">
          <div v-if="band" class="sky-band" :style="band"></div>
          <svg class="sky-dust" width="100%" height="100%" focusable="false">
            <circle v-for="(d, i) in dust" :key="'d' + i" :cx="d.x" :cy="d.y" :r="d.r" :opacity="d.o"/>
          </svg>
        </div>
        <span v-for="(s, i) in stars" :key="'st' + i" class="star" :style="s.style">
          <span class="star-dot" :class="s.cls" :style="s.dotStyle"></span>
        </span>
        <div ref="meteor" class="meteor">
          <div class="meteor-move">
            <span class="meteor-tail"></span>
            <span class="meteor-head"></span>
          </div>
        </div>
      </div>

      <div class="sky-orb">
        <!-- The halo and the core fade separately, so a setting sun melts
             into its own warm glow instead of greying out over the night. -->
        <div class="sun">
          <div class="sun-halo">
            <div class="sun-rays"></div>
            <div class="sun-bloom">
              <div class="sun-glow"></div>
              <div class="sun-glow-warm"></div>
            </div>
          </div>
          <div class="sun-core">
            <div class="sun-disc"></div>
            <div class="sun-corona"></div>
            <div class="sun-tint"></div>
          </div>
        </div>

        <div class="moon">
          <div class="moon-bloom"><div class="moon-halo"></div></div>
          <svg class="moon-svg" viewBox="0 0 120 120" focusable="false">
            <defs>
              <radialGradient :id="sid('mb')" cx="0.62" cy="0.36" r="0.72">
                <stop offset="0" stop-color="#ffffff"/>
                <stop offset="0.42" stop-color="#eef3ff"/>
                <stop offset="0.82" stop-color="#dfe6f5"/>
                <stop offset="1" stop-color="#d0d9ec"/>
              </radialGradient>
              <!-- Lit from the right, a crater is a dent: its far (left)
                   wall catches the light and the near (right) wall is in
                   shadow, under a bright outer rim (drawn separately). -->
              <linearGradient :id="sid('mc')" x1="0" y1="0.2" x2="1" y2="0.8">
                <stop offset="0" stop-color="#f2f5fc"/>
                <stop offset="0.45" stop-color="#d8dfee"/>
                <stop offset="1" stop-color="#a9b5cf"/>
              </linearGradient>
              <!-- Gibbous terminator: shade outside a circle centred right of
                   the disc, so the boundary curves through the poles like the
                   real one, and falls off softly into earthshine. -->
              <radialGradient :id="sid('mt')" cx="80" cy="60" r="74" gradientUnits="userSpaceOnUse">
                <stop offset="0" stop-color="#121b33" stop-opacity="0"/>
                <stop offset="0.58" stop-color="#121b33" stop-opacity="0"/>
                <stop offset="0.7" stop-color="#121b33" stop-opacity="0.2"/>
                <stop offset="0.8" stop-color="#121b33" stop-opacity="0.44"/>
                <stop offset="0.9" stop-color="#121b33" stop-opacity="0.62"/>
                <stop offset="1" stop-color="#121b33" stop-opacity="0.7"/>
              </radialGradient>
              <!-- The limb only glints on the lit side. -->
              <linearGradient :id="sid('mr')" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stop-color="#ffffff" stop-opacity="0"/>
                <stop offset="0.4" stop-color="#ffffff" stop-opacity="0.1"/>
                <stop offset="1" stop-color="#ffffff" stop-opacity="0.7"/>
              </linearGradient>
              <clipPath :id="sid('mclip')">
                <circle cx="60" cy="60" r="50"/>
              </clipPath>
              <filter :id="sid('msoft')" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="2.4"/>
              </filter>
            </defs>
            <circle cx="60" cy="60" r="50" :fill="surl('mb')"/>
            <g :clip-path="surl('mclip')">
              <!-- Maria: the soft grey "seas" that make it read as the Moon. -->
              <g class="moon-maria" :filter="surl('msoft')">
                <path d="M24,38 C30,24 50,22 57,32 C63,41 56,52 45,53 C35,54 28,58 23,52 C19,47 20,43 24,38 Z"/>
                <path d="M62,34 C68,26 82,27 86,36 C89,43 84,50 76,50 C69,50 60,42 62,34 Z"/>
                <path d="M66,56 C73,50 88,54 91,64 C93,72 86,79 77,77 C69,75 61,63 66,56 Z"/>
                <path d="M30,64 C36,59 48,61 50,69 C52,77 44,83 36,80 C29,77 26,69 30,64 Z"/>
              </g>
              <g class="moon-maria moon-maria--deep" :filter="surl('msoft')">
                <ellipse cx="40" cy="38" rx="9" ry="6"/>
                <ellipse cx="75" cy="38" rx="6" ry="5"/>
                <ellipse cx="79" cy="65" rx="6.5" ry="5"/>
              </g>
              <g v-for="(c, i) in craters" :key="'mc' + i" :opacity="c.o">
                <circle :cx="c.cx" :cy="c.cy" :r="c.r" :fill="surl('mc')"/>
                <path class="moon-crater-rim" :d="c.rim" :stroke-width="c.sw"/>
              </g>
              <!-- A young, bright ray crater low on the disc. -->
              <circle class="moon-bright" cx="56" cy="94" r="2.6" :filter="surl('msoft')"/>
              <rect x="0" y="0" width="120" height="120" :fill="surl('mt')"/>
            </g>
            <circle class="moon-rim" cx="60" cy="60" r="49.4" :stroke="surl('mr')"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- Cloud layer (parallax 8px / 4px). Each lane drifts left to right and
         loops; negative delays populate the sky from the first frame. Every
         lane holds a day cloud and a night veil that crossfade. Both href
         forms: WebKit before Safari 12.1 / iOS 12.2 only reads xlink:href. -->
    <div class="sky-clouds">
      <div v-for="(c, i) in clouds" :key="'cl' + i" class="cloud-lane" :style="c.laneStyle">
        <div class="cloud-in" :style="c.inStyle">
          <div class="cloud-day">
            <svg class="cloud-svg" :class="{ 'is-flipped': c.flip }" viewBox="0 0 240 100" focusable="false">
              <use :href="c.dayRef" :xlink:href="c.dayRef" :filter="surl(c.dayFilter)"/>
            </svg>
          </div>
          <div class="cloud-night">
            <svg class="cloud-svg" viewBox="0 0 240 100" focusable="false">
              <use :href="c.nightRef" :xlink:href="c.nightRef" :filter="surl(c.nightFilter)"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Glare in front of the clouds (same anchor and depth as the sun and
         moon): a cloud drifting near them catches their light, a silver
         lining at night and a sunlit edge by day. -->
    <div class="sky-far sky-far--glare">
      <div class="sky-orb">
        <div class="sun sun-glare"></div>
        <div class="moon moon-glare"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { seededRandom, clamp, exposeDebug, removeDebug } from './heroMotion';

// How long the entrance animations need (the last stars finish at ~3.1 s).
// After that the entrance classes are dropped, so nothing can replay them.
const ENTRANCE_MS = 3400;

function fixed(n, digits) {
  return Number(n.toFixed(digits));
}

// Cirrus streaks [cx, cy, rx, ry, rotation] in a 240x100 box: the thin,
// high cloud that keeps the sky from being all puffs.
const CIRRUS = Object.freeze([
  [118, 52, 104, 4.2, -5], [86, 60, 66, 3.2, -7], [156, 44, 76, 3, -3],
  [188, 58, 46, 2.6, -6], [64, 46, 40, 2.4, -3], [132, 64, 58, 2.2, -4]
]);

// Day cumulus in a 240x100 box: flattened `base` ellipses give a gently
// scalloped underside, `lobes` the billowing body and `caps` the soft
// highlights that round the front lobes.
const DAY_CLOUDS = Object.freeze([
  {
    base: [[120, 77, 96, 9], [64, 75, 40, 8], [176, 76, 40, 7]],
    lobes: [[44, 67, 19], [74, 53, 28], [113, 41, 34], [153, 47, 30], [186, 59, 23], [210, 69, 14], [96, 65, 22], [138, 65, 24]],
    caps: [[78, 48, 22], [117, 35, 27], [157, 43, 23], [189, 56, 16]]
  },
  {
    base: [[124, 79, 102, 8], [78, 78, 48, 7]],
    lobes: [[30, 73, 14], [56, 63, 21], [90, 55, 26], [126, 51, 29], [162, 57, 24], [194, 65, 18], [218, 72, 11], [108, 69, 20], [146, 70, 19]],
    caps: [[59, 59, 15], [93, 50, 19], [129, 45, 22], [165, 53, 17], [196, 62, 12]]
  },
  {
    base: [[130, 79, 70, 8]],
    lobes: [[70, 68, 19], [100, 52, 30], [138, 46, 28], [168, 60, 22], [190, 71, 13], [120, 68, 22]],
    caps: [[103, 46, 22], [141, 40, 21], [171, 56, 15]]
  },
  { streaks: CIRRUS }
]);

// Night veils [cx, cy, rx, ry, rotation]: overlapping translucent, slightly
// tilted ellipses (denser where they overlap, lumpy on top, tapering at the
// ends) with moonlit rims along the top.
const NIGHT_CLOUDS = Object.freeze([
  {
    body: [[70, 60, 46, 9, -4], [118, 54, 52, 14, -2], [160, 50, 40, 12, 3], [196, 56, 30, 7, 6], [100, 64, 60, 7, 0], [146, 62, 50, 8, 2], [40, 64, 26, 5, -6]],
    rims: [[128, 43, 30, 4, -2], [166, 40, 22, 3.5, 3]]
  },
  {
    body: [[60, 60, 50, 5, -3], [112, 56, 56, 8, -1], [164, 54, 48, 6, 2], [204, 57, 30, 4, 5], [130, 62, 70, 4, 0]],
    rims: [[150, 49, 34, 2.8, 1]]
  },
  {
    body: [[100, 58, 36, 10, -3], [132, 52, 34, 13, 2], [162, 58, 26, 8, 6], [80, 63, 22, 6, -5], [124, 64, 50, 6, 0]],
    rims: [[140, 41, 22, 3.5, 2]]
  },
  { streaks: CIRRUS }
]);

// Blur per cloud kind, in the cloud's own 240x100 units.
const CLOUD_FILTERS = Object.freeze([
  { id: 'cfd', blur: 0.7 },
  { id: 'cfdf', blur: 1.5 },
  { id: 'cfc', blur: 1.4 },
  { id: 'cfn', blur: 3 },
  { id: 'cfnf', blur: 4 }
]);

// Aurora curtains in a 1200x400 box (stretched to each ribbon): `shape` is the
// whole curtain, `edge` its bright lower hem.
const CURTAINS = Object.freeze([
  {
    shape: 'M0,292 C120,258 230,300 360,286 C500,270 600,232 740,250 C880,268 990,318 1200,272 L1200,96 C1040,140 900,70 760,96 C610,124 480,64 340,92 C200,120 110,78 0,110 Z',
    edge: 'M0,292 C120,258 230,300 360,286 C500,270 600,232 740,250 C880,268 990,318 1200,272'
  },
  {
    shape: 'M0,252 C160,288 300,238 450,262 C610,286 720,300 860,270 C980,246 1080,232 1200,256 L1200,66 C1060,40 940,110 800,86 C650,60 540,120 390,100 C250,82 140,40 0,64 Z',
    edge: 'M0,252 C160,288 300,238 450,262 C610,286 720,300 860,270 C980,246 1080,232 1200,256'
  }
]);

const AURORA_COLORS = Object.freeze(['#4ade80', '#2dd4bf']);

// Moon craters [cx, cy, r, opacity] in the moon's 120x120 box (disc r=50 at
// 60,60), each with a bright rim arc on its sunward (right) side.
const CRATERS = Object.freeze([
  [70, 88, 5.6, 0.92], [88, 44, 3.4, 0.86], [50, 22, 3, 0.8], [60, 60, 2.4, 0.78], [26, 70, 2.6, 0.7],
  [84, 82, 2.2, 0.82], [38, 88, 2, 0.72], [97, 64, 1.8, 0.76], [76, 21, 1.6, 0.7]
].map((c) => {
  const R = c[2] * 1.06;
  return {
    cx: c[0],
    cy: c[1],
    r: c[2],
    o: c[3],
    sw: fixed(Math.min(c[2] * 0.2, 0.9), 2),
    rim: 'M' + c[0] + ',' + fixed(c[1] - R, 2) + ' A' + fixed(R, 2) + ',' + fixed(R, 2) + ' 0 0 1 ' + c[0] + ',' + fixed(c[1] + R, 2)
  };
}));

// Hand-placed so the sky reads composed at t=0: clouds start toward the sides
// (`phase` = how far through its crossing each one is) and stay in the upper
// ~40%. The third one drifts across the sun / moon soon after load. `depth`
// 0 is far (small, slow, softer), 1 is near. `day`/`night` pick the shapes
// (3 = cirrus); `od`/`on` are the day / night opacities (0 hides it).
const CLOUD_LAYOUT = [
  { day: 0, night: 0, size: 1, y: 6.5, depth: 1, phase: 0.13, od: 0.96, on: 0.62, flip: false },
  { day: 1, night: 1, size: 0.8, y: 21, depth: 0.7, phase: 0.85, od: 0.9, on: 0.52, flip: true },
  { day: 2, night: 2, size: 0.52, y: 8.5, depth: 0.32, phase: 0.68, od: 0.88, on: 0.66, flip: false },
  { day: 3, night: 3, size: 0.95, y: 29, depth: 0.45, phase: 0.03, od: 0.7, on: 0.42, flip: false },
  { day: 2, night: 1, size: 0.42, y: 15, depth: 0.15, phase: 0.34, od: 0.74, on: 0.42, flip: true },
  { day: 0, night: 0, size: 0.66, y: 35, depth: 0.55, phase: 0.95, od: 0.84, on: 0, flip: true }
];

const RIBBON_LAYOUT = [
  { curtain: 0, color: 0, left: -12, width: 70, top: -2, rotate: -7, opacity: 0.18, wave: 'a', dur: 26, breathe: 11 },
  { curtain: 1, color: 1, left: 40, width: 72, top: 4, rotate: 5, opacity: 0.15, wave: 'b', dur: 33, breathe: 14 },
  { curtain: 0, color: 1, left: 8, width: 86, top: -11, rotate: -2, opacity: 0.09, wave: 'b', dur: 41, breathe: 17 }
];

const STAR_TINTS = [
  { core: '#ffffff', glow: 'rgba(222, 232, 255, 0.6)' },
  { core: '#dfe8ff', glow: 'rgba(190, 210, 255, 0.55)' },
  { core: '#ffe7c7', glow: 'rgba(255, 212, 160, 0.5)' },
  { core: '#cddcff', glow: 'rgba(150, 182, 255, 0.55)' }
];

// Every twinkling star is its own compositor layer, so big skies keep the
// twinkle on the brightest ones only (the faint rest barely shows it).
const TWINKLE_MAX = 64;

// Phones held sideways (mirrors Home.vue and the .sky-orb rule below).
const LAND_PHONE = '(max-height: 520px) and (orientation: landscape)';

// A cloud thins out while it crosses the centre column (the title): opacity
// over its crossing, as in the sky-drift keyframes. Used for the resting
// cloud under reduced motion.
function cloudThin(phase) {
  const dim = 0.42;
  if (phase <= 0.27 || phase >= 0.73) return 1;
  if (phase >= 0.42 && phase <= 0.58) return dim;
  const t = phase < 0.5 ? (phase - 0.27) / 0.15 : (0.73 - phase) / 0.15;
  return fixed(1 - (1 - dim) * t, 3);
}

export default {
  name: 'HeroSky',
  inject: ['heroState'],
  data() {
    return {
      uid: 'hsky' + this._uid,
      // The entrance runs once per visit, never under reduced motion.
      entering: !this.heroState.reducedMotion,
      stars: [],
      dust: [],
      band: null,
      clouds: [],
      ribbons: [],
      dayClouds: DAY_CLOUDS,
      nightClouds: NIGHT_CLOUDS,
      cloudFilters: CLOUD_FILTERS,
      curtains: CURTAINS,
      auroraColors: AURORA_COLORS,
      craters: CRATERS
    };
  },
  computed: {
    still() {
      return this.heroState.reducedMotion;
    }
  },
  watch: {
    // The landscape band follows the viewport height too (vh in Home.vue),
    // so either size can move the star box.
    'heroState.width'() {
      this.refit();
    },
    'heroState.height'() {
      this.refit();
    },
    'heroState.running'(running) {
      if (running) {
        this.resumeMeteors();
        this.resumeEntrance();
      } else {
        this.pauseMeteors();
        this.pauseEntrance();
      }
    },
    still(still) {
      // Reduced motion switched on: show the composed sky and never replay
      // the entrance afterwards.
      if (still) this.endEntrance();
    }
  },
  created() {
    this.builtWidth = 0;
    this.builtGeo = null;
    this.measured = false;
    this.meteorTimer = 0;
    this.meteorDue = 0;
    this.meteorRand = seededRandom(0x6d657465);
    // The first shooting star is the entrance's finale.
    this.meteorLeft = 3000 + this.meteorRand() * 400;
    this.meteorFirst = true;
    this.enterTimer = 0;
    this.enterDue = 0;
    this.enterLeft = ENTRANCE_MS;
    this.debugFrozen = false;
    this.build();
  },
  mounted() {
    // Laid out now (and not painted yet): refit the stars to the real box.
    this.refit();
    this.resumeMeteors();
    this.resumeEntrance();
    exposeDebug('sky', {
      pause: () => this.debugPause(),
      resume: () => this.debugResume(),
      renderAt: (ms) => this.debugRenderAt(ms),
      shoot: () => this.fireMeteor(),
      info: () => ({
        width: this.builtWidth,
        measured: this.measured,
        starBox: this.builtGeo ? [Math.round(this.builtGeo.areaW), Math.round(this.builtGeo.areaH)] : null,
        twinkling: this.stars.filter((s) => s.cls).length,
        stars: this.stars.length,
        dust: this.dust.length,
        clouds: this.clouds.length,
        ribbons: this.ribbons.length,
        entering: this.entering,
        nextMeteorIn: this.meteorTimer ? Math.round(this.meteorDue - performance.now()) : null
      })
    });
  },
  beforeDestroy() {
    clearTimeout(this.meteorTimer);
    clearTimeout(this.enterTimer);
    this.meteorTimer = 0;
    this.enterTimer = 0;
    removeDebug('sky');
  },
  methods: {
    sid(name) {
      return this.uid + '-' + name;
    },
    surl(name) {
      return 'url(#' + this.uid + '-' + name + ')';
    },
    rot(e) {
      return 'rotate(' + e[4] + ' ' + e[0] + ' ' + e[1] + ')';
    },

    // `measured` is the real star box (measureGeometry); without it (before
    // mount, or while hidden) the geometry is estimated from the hero size.
    build(measured) {
      const width = measured
        ? Math.max(Math.round(measured.areaW) - 32, 280)
        : Math.max(this.heroState.width || 1200, 280);
      const height = Math.max(this.heroState.height || 640, 360);
      const geo = measured || this.geometry(width, height);
      this.builtWidth = width;
      this.builtGeo = geo;
      this.stars = this.makeStars(geo);
      this.dust = this.makeDust(geo);
      this.band = this.makeBand(geo);
      this.clouds = this.makeClouds(width);
      this.ribbons = this.makeRibbons(width);
    },

    // The first measurement always refits (the estimate can't know the real
    // landscape band); after that only a meaningful change does (rotation,
    // window resize, the band growing with the height), never every pixel.
    refit() {
      const geo = this.measureGeometry();
      if (!geo) {
        // Not laid out (hidden): keep the estimate in step with the width.
        const width = Math.max(this.heroState.width || 1200, 280);
        if (!this.builtWidth || Math.abs(width - this.builtWidth) / this.builtWidth > 0.15) {
          this.build();
        }
        return;
      }
      const old = this.builtGeo;
      const moved = (a, b, tolerance) => !b || Math.abs(a - b) / b > tolerance;
      if (!this.measured) {
        this.measured = true;
        if (!old || moved(geo.areaW, old.areaW, 0.01) || moved(geo.areaH, old.areaH, 0.01) ||
            moved(geo.orb, old.orb, 0.01) || Math.hypot(geo.orbX - old.orbX, geo.orbY - old.orbY) > 4) {
          this.build(geo);
        }
        return;
      }
      if (moved(geo.areaW, old.areaW, 0.15) || moved(geo.areaH, old.areaH, 0.15) || moved(geo.orb, old.orb, 0.15)) {
        this.build(geo);
      }
    },

    // The star box and the sun / moon anchor in px, read from the layout:
    // .sky-stars and .sky-orb share the .sky-far offset parent, and offset
    // sizes ignore the entrance transforms.
    measureGeometry() {
      const el = this.$el;
      if (!el || !el.querySelector) return null;
      const box = el.querySelector('.sky-stars');
      const orbEl = el.querySelector('.sky-orb');
      if (!box || !orbEl) return null;
      const areaW = box.clientWidth;
      const areaH = box.clientHeight;
      const orb = orbEl.offsetWidth;
      if (areaW < 100 || areaH < 60 || !orb) return null;
      return {
        areaW,
        areaH,
        orb,
        orbX: orbEl.offsetLeft - box.offsetLeft + orb / 2,
        orbY: orbEl.offsetTop - box.offsetTop + orbEl.offsetHeight / 2
      };
    },

    // The pre-mount estimate of measureGeometry, mirroring the CSS (.sky-far
    // overscan, .sky-stars insets, .sky-orb breakpoints, Home.vue's
    // --hero-land-h). Replaced by the measured box before the first paint.
    geometry(width, height) {
      const vw = window.innerWidth || width;
      const vh = window.innerHeight || height;
      const landPhone = !!(window.matchMedia && window.matchMedia(LAND_PHONE).matches);
      const farH = height + 24;
      const areaW = width + 32;
      const landH = landPhone ? clamp(vh * 0.32, 104, 150) : clamp(Math.max(vw * 0.17, vh * 0.24), 150, 330);
      const areaH = Math.max(farH * 0.985 - landH * 0.8, 160);
      const tiny = width <= 420;
      const phone = width < 576;
      let orb = tiny ? clamp(width * 0.13, 44, 64) : clamp(width * 0.07, 64, 110);
      let top = tiny ? 0.03 : phone ? 0.05 : 0.09;
      let right = phone ? 0.08 : 0.13;
      if (landPhone) {
        orb = 46;
        top = 0.06;
        right = 0.05;
      }
      return {
        areaW,
        areaH,
        orb,
        // Orb centre in the star box (which starts 1.5% below the far layer's top).
        orbX: areaW * (1 - right) - orb / 2,
        orbY: farH * (top - 0.015) + orb / 2
      };
    },

    // Blue-noise star field (best-candidate sampling, so no clumps or holes),
    // kept clear of the moon and of the landscape band. The count follows the
    // sky's area, so tall phone and tablet skies are as rich as wide ones.
    makeStars(g) {
      const rand = seededRandom(0x5eed51);
      const count = Math.round(clamp((g.areaW * g.areaH) / 6500, 40, 200));
      const lowDpr = (window.devicePixelRatio || 1) < 1.5;
      const keepOut = g.orb;
      const maxGlints = Math.max(3, Math.round(count / 16));
      // Where shooting stars must not start (they would seem to come out of the moon).
      this.moonZone = {
        x: g.orbX / g.areaW,
        y: g.orbY / g.areaH,
        rx: (g.orb * 1.8) / g.areaW,
        ry: (g.orb * 1.8) / g.areaH
      };

      const points = [];
      for (let i = 0; i < count; i++) {
        let best = null;
        let bestScore = -1;
        for (let k = 0; k < 8; k++) {
          const x = rand();
          const y = Math.pow(rand(), 1.18);
          const px = x * g.areaW;
          const py = y * g.areaH;
          if (Math.hypot(px - g.orbX, py - g.orbY) < keepOut) continue;
          let d = Infinity;
          for (let p = 0; p < points.length; p++) {
            const dx = points[p].px - px;
            const dy = points[p].py - py;
            d = Math.min(d, dx * dx + dy * dy);
          }
          if (d > bestScore) {
            bestScore = d;
            best = { x, y, px, py };
          }
        }
        if (best) points.push(best);
      }

      let glints = 0;
      const brightness = [];
      const stars = points.map((p) => {
        let size = 0.9 + Math.pow(rand(), 2.4) * 1.6;
        // Below ~1 device pixel a star just vanishes on standard screens.
        if (lowDpr) size = Math.max(size, 1);
        let bright = clamp(0.45 + ((size - 0.9) / 1.6) * 0.45 + rand() * 0.2, 0.45, 1);
        // Lower stars sit in thicker air: dimmer toward the horizon.
        bright *= 1 - 0.3 * p.y * p.y;

        const tintRoll = rand();
        const tint = STAR_TINTS[tintRoll < 0.06 ? 2 : tintRoll < 0.12 ? 3 : tintRoll < 0.5 ? 1 : 0];

        const glint = glints < maxGlints && size > 1.6 && Math.abs(p.x - 0.5) > 0.21 && p.y < 0.75;
        if (glint) {
          glints++;
          size = Math.max(size, 2);
          bright = Math.max(bright, 0.92);
        }

        const twRoll = rand();
        const cls = glint ? 'tw-g' : twRoll < 0.42 ? 'tw-a' : twRoll < 0.68 ? 'tw-b' : '';
        const twDur = glint ? 5 + rand() * 3 : 2.6 + rand() * 4.4;
        const twDelay = -rand() * twDur;

        // The brightest stars come out first at dusk (and go last at dawn).
        const toggleDelay = (1 - bright) * 1.15 + rand() * 0.3;
        const dawnDelay = bright * 0.45 + rand() * 0.15;
        const enterDelay = Math.min(0.3 + (1 - bright) * 1.45 + rand() * 0.4, 2.2);

        const dotStyle = {
          background: tint.core,
          animationDuration: fixed(twDur, 2) + 's',
          animationDelay: fixed(twDelay, 2) + 's'
        };
        if (size > 1.35) {
          dotStyle.boxShadow = '0 0 ' + fixed(size * 2.6, 1) + 'px ' + fixed(size * 0.35, 1) + 'px ' + tint.glow;
        }
        if (glint) {
          dotStyle['--s'] = fixed(size, 2) + 'px';
        }
        brightness.push(bright);

        return {
          cls,
          dotStyle,
          style: {
            left: fixed(p.x * 100, 3) + '%',
            top: fixed(p.y * 100, 3) + '%',
            width: fixed(size, 2) + 'px',
            height: fixed(size, 2) + 'px',
            marginLeft: fixed(-size / 2, 2) + 'px',
            marginTop: fixed(-size / 2, 2) + 'px',
            '--o': fixed(bright, 3),
            '--d': fixed(toggleDelay, 2) + 's',
            '--d2': fixed(dawnDelay, 2) + 's',
            '--e': fixed(enterDelay, 2) + 's'
          }
        };
      });

      // Over the twinkle budget (wide skies only), the faintest twinklers
      // hold still. Done after the fact, so the field itself never changes.
      const twinkling = [];
      stars.forEach((s, i) => {
        if (s.cls === 'tw-a' || s.cls === 'tw-b') twinkling.push(i);
      });
      if (twinkling.length > TWINKLE_MAX) {
        twinkling.sort((a, b) => brightness[a] - brightness[b]);
        twinkling.slice(0, twinkling.length - TWINKLE_MAX).forEach((i) => {
          stars[i].cls = '';
        });
      }
      return stars;
    },

    // The Milky Way's axis: from low on the left up to the top, well clear of
    // the moon. Shared by the dust (denser along it) and the band's glow.
    bandAxis(g) {
      const x0 = 0;
      const y0 = g.areaH * 0.74;
      const x1 = g.areaW * 0.7;
      const y1 = -g.areaH * 0.06;
      const len = Math.hypot(x1 - x0, y1 - y0);
      return {
        x0,
        y0,
        dx: (x1 - x0) / len,
        dy: (y1 - y0) / len,
        len,
        spread: clamp(Math.hypot(g.areaW, g.areaH) * 0.075, 50, 150)
      };
    },

    // Hundreds of faint, static micro-stars: depth under the twinkling ones,
    // in a single SVG node.
    makeDust(g) {
      const rand = seededRandom(0xd0571);
      const axis = this.bandAxis(g);
      const n = Math.round(clamp((g.areaW * g.areaH) / 3600, 90, 420));
      const dust = [];
      for (let i = 0; i < n * 2 && dust.length < n; i++) {
        let x;
        let y;
        if (rand() < 0.45) {
          const t = (-0.05 + rand() * 1.1) * axis.len;
          // Sum of three uniforms: a cheap bell curve across the band.
          const off = (rand() + rand() + rand() - 1.5) * axis.spread;
          x = axis.x0 + axis.dx * t - axis.dy * off;
          y = axis.y0 + axis.dy * t + axis.dx * off;
        } else {
          x = rand() * g.areaW;
          y = Math.pow(rand(), 1.1) * g.areaH;
        }
        if (x < 0 || x > g.areaW || y < 0 || y > g.areaH) continue;
        if (Math.hypot(x - g.orbX, y - g.orbY) < g.orb * 0.9) continue;
        const depth = y / g.areaH;
        dust.push({
          x: fixed((x / g.areaW) * 100, 2) + '%',
          y: fixed((y / g.areaH) * 100, 2) + '%',
          r: fixed(0.38 + rand() * 0.32, 2),
          o: fixed((0.22 + rand() * 0.3) * (1 - 0.45 * depth * depth), 2)
        });
      }
      return dust;
    },

    // The band's faint glow: one static, rotated gradient.
    makeBand(g) {
      const axis = this.bandAxis(g);
      const cx = axis.x0 + (axis.dx * axis.len) / 2;
      const cy = axis.y0 + (axis.dy * axis.len) / 2;
      const w = axis.len * 1.3;
      const h = axis.spread * 3.2;
      return {
        left: fixed(((cx - w / 2) / g.areaW) * 100, 2) + '%',
        top: fixed(((cy - h / 2) / g.areaH) * 100, 2) + '%',
        width: fixed((w / g.areaW) * 100, 2) + '%',
        height: Math.round(h) + 'px',
        transform: 'rotate(' + fixed((Math.atan2(axis.dy, axis.dx) * 180) / Math.PI, 2) + 'deg)'
      };
    },

    makeClouds(width) {
      const count = width < 600 ? 3 : width < 1100 ? 4 : width < 1700 ? 5 : 6;
      const baseW = clamp(110 + width * 0.13, 150, 420);
      return CLOUD_LAYOUT.slice(0, count).map((c, i) => {
        const cw = Math.round(baseW * c.size);
        const ch = Math.round((cw * 100) / 240);
        // The crossing time depends on depth only, so near clouds stay about
        // twice as fast as far ones at every width (parallax in the motion).
        const duration = clamp(158 - c.depth * 92, 60, 160);
        const far = c.depth < 0.4;
        return {
          dayRef: '#' + this.sid('cd' + c.day),
          nightRef: '#' + this.sid('cn' + c.night),
          flip: c.flip,
          dayFilter: c.day === 3 ? 'cfc' : far ? 'cfdf' : 'cfd',
          nightFilter: c.night === 3 ? 'cfc' : far ? 'cfnf' : 'cfn',
          laneStyle: {
            '--cw': cw + 'px',
            top: c.y + '%',
            animationDuration: fixed(duration, 1) + 's',
            animationDelay: fixed(-c.phase * duration, 1) + 's',
            // The resting place under reduced motion (the drift overrides
            // both).
            transform: 'translate3d(' + fixed(c.phase * 100, 2) + '%, 0, 0)',
            opacity: cloudThin(c.phase)
          },
          inStyle: {
            width: cw + 'px',
            height: ch + 'px',
            animationDelay: fixed(0.08 + i * 0.14, 2) + 's',
            '--od': c.od,
            '--on': c.on
          }
        };
      });
    },

    makeRibbons(width) {
      const rand = seededRandom(0xa0a0a);
      const count = width < 900 ? 2 : 3;
      return RIBBON_LAYOUT.slice(0, count).map((r) => {
        // Soft, irregular light pillars (thin crisp ones read as a grid).
        const rays = [];
        for (let i = 0; i < 30; i++) {
          rays.push({
            x: Math.round(rand() * 1200),
            w: Math.round(7 + rand() * 20),
            o: fixed(0.14 + Math.pow(rand(), 1.6) * 0.56, 2)
          });
        }
        return {
          curtain: r.curtain,
          color: r.color,
          wave: r.wave,
          rays,
          style: {
            left: r.left + '%',
            width: r.width + '%',
            top: r.top + '%',
            opacity: r.opacity,
            transform: 'rotate(' + r.rotate + 'deg)'
          },
          waveStyle: {
            animationDuration: r.dur + 's',
            animationDelay: fixed(-rand() * r.dur, 1) + 's'
          },
          breatheStyle: {
            animationDuration: r.breathe + 's',
            animationDelay: fixed(-rand() * r.breathe, 1) + 's'
          }
        };
      });
    },

    // The entrance classes stay on for ENTRANCE_MS of running time (the timer
    // pauses with the scene), then go, so the entrance can never replay.
    resumeEntrance() {
      if (!this.entering || this.enterTimer || this.debugFrozen || !this.heroState.running) return;
      this.enterDue = performance.now() + this.enterLeft;
      this.enterTimer = setTimeout(() => {
        this.enterTimer = 0;
        this.enterLeft = 0;
        this.entering = false;
      }, this.enterLeft);
    },
    pauseEntrance() {
      if (!this.enterTimer) return;
      this.enterLeft = Math.max(0, this.enterDue - performance.now());
      clearTimeout(this.enterTimer);
      this.enterTimer = 0;
    },
    endEntrance() {
      clearTimeout(this.enterTimer);
      this.enterTimer = 0;
      this.enterLeft = 0;
      this.entering = false;
    },

    // Shooting stars: one every ~9-16 s. The timer keeps its remaining time
    // across pauses (off-screen, hidden tab) so it never fires in a burst.
    // In light mode the layer is transparent, so no theme read is needed.
    scheduleMeteor(ms) {
      clearTimeout(this.meteorTimer);
      this.meteorDue = performance.now() + ms;
      this.meteorTimer = setTimeout(() => {
        this.meteorTimer = 0;
        this.fireMeteor();
        this.scheduleMeteor(9000 + this.meteorRand() * 7000);
      }, ms);
    },
    pauseMeteors() {
      if (!this.meteorTimer) return;
      this.meteorLeft = Math.max(0, this.meteorDue - performance.now());
      clearTimeout(this.meteorTimer);
      this.meteorTimer = 0;
    },
    resumeMeteors() {
      if (this.meteorTimer || this.debugFrozen || this.heroState.reducedMotion || !this.heroState.running) return;
      const wait = this.meteorLeft != null ? this.meteorLeft : 9000 + this.meteorRand() * 7000;
      this.meteorLeft = null;
      this.scheduleMeteor(wait);
    },
    fireMeteor() {
      const el = this.$refs.meteor;
      if (!el) return;
      const rand = this.meteorRand;
      const width = this.heroState.width || 1200;
      const first = this.meteorFirst;
      this.meteorFirst = false;
      let headingRight;
      let x;
      let y;
      let angle;
      let travel;
      if (first) {
        // The finale of the entrance: high in the left third, heading right,
        // done before it reaches the title column.
        headingRight = true;
        x = 0.06 + rand() * 0.1;
        y = 0.05 + rand() * 0.1;
        angle = 16 + rand() * 10;
        travel = clamp(width * 0.2, 140, 380);
      } else {
        headingRight = rand() < 0.5;
        const zone = this.moonZone;
        x = 0.5;
        y = 0.2;
        for (let tries = 0; tries < 8; tries++) {
          x = headingRight ? 0.05 + rand() * 0.4 : 0.55 + rand() * 0.4;
          y = 0.03 + rand() * 0.3;
          const dx = (x - zone.x) / zone.rx;
          const dy = (y - zone.y) / zone.ry;
          if (dx * dx + dy * dy > 1) break;
        }
        angle = 14 + rand() * 20;
        travel = clamp(width * (0.16 + rand() * 0.12), 140, 520);
      }
      const tail = clamp(travel * (0.4 + rand() * 0.18), 64, 210);
      const duration = 0.78 + rand() * 0.3;

      el.style.left = fixed(x * 100, 2) + '%';
      el.style.top = fixed(y * 100, 2) + '%';
      el.style.transform = 'rotate(' + fixed(headingRight ? angle : 180 - angle, 1) + 'deg)';
      el.style.setProperty('--mt', Math.round(travel) + 'px');
      el.style.setProperty('--ml', Math.round(tail) + 'px');
      el.style.setProperty('--md', fixed(duration, 2) + 's');
      // Restart the CSS animation.
      el.classList.remove('is-flying');
      void el.offsetWidth;
      el.classList.add('is-flying');
    },

    // Debug helpers (window.__hero.sky): everything here is CSS-driven, so
    // freezing and seeking act on this component's own animations.
    animations() {
      const el = this.$el;
      if (!el) return [];
      if (el.getAnimations) return el.getAnimations({ subtree: true });
      return document.getAnimations().filter((a) => a.effect && el.contains(a.effect.target));
    },
    debugPause() {
      this.debugFrozen = true;
      this.pauseMeteors();
      this.pauseEntrance();
      this.animations().forEach((a) => a.pause());
    },
    debugResume() {
      this.debugFrozen = false;
      this.animations().forEach((a) => {
        // play() would rewind a one-shot (entrance) animation seeked past its
        // end, replaying it; finish those instead.
        const timing = a.effect && a.effect.getComputedTiming();
        if (timing && isFinite(timing.endTime) && a.currentTime >= timing.endTime) {
          a.finish();
        } else {
          a.play();
        }
      });
      this.resumeMeteors();
      this.resumeEntrance();
    },
    debugRenderAt(ms) {
      this.debugFrozen = true;
      this.pauseMeteors();
      this.pauseEntrance();
      // Bring the entrance back (synchronously, so its animations exist
      // right now) when it already ran, so any moment of it can be seen.
      if (!this.still && this.$el) {
        this.entering = true;
        this.$el.classList.add('is-entering');
        this.enterLeft = Math.max(0, ENTRANCE_MS - ms);
      }
      this.animations().forEach((a) => {
        a.pause();
        a.currentTime = ms;
      });
    }
  }
};
</script>

<style scoped lang="scss">
$light: '[data-theme="light"]';
$dark: 'html:not([data-theme="light"])';
$ease-out: cubic-bezier(0.16, 1, 0.3, 1);
$ease-soft: cubic-bezier(0.22, 1, 0.36, 1);
$ease-in: cubic-bezier(0.5, 0, 0.75, 0);
$ease-io: cubic-bezier(0.45, 0.05, 0.55, 0.95);
$ease-sink: cubic-bezier(0.45, 0, 0.55, 1);
// Front-loaded: half the change is done in the first quarter, so the sky
// keeps up with the hero text, which flips colour instantly.
$ease-front: cubic-bezier(0.2, 0.75, 0.35, 1);
// Gentler than $ease-out, so the entrance rise reads as a rise, not a pop.
$ease-rise: cubic-bezier(0.3, 0.7, 0.35, 1);

.hero-sky {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  pointer-events: none;
  background: #081522;
  // How far the sun sinks at sunset (and rises from at sunrise).
  --sky-set: 120px;
}

#{$light} .hero-sky {
  background: #d9eef0;
}

@media (max-width: 575px) {
  .hero-sky {
    --sky-set: 72px;
  }
}

.sky-defs {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}

/* ---------------------------------------------------------------------- */
/* Sky fills                                                              */
/* ---------------------------------------------------------------------- */

.sky-fill,
.sky-tw,
.sky-tw-in,
.sky-tw-fill,
.sky-grain,
.sky-wake {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

// Every sky is a cool base gradient in % plus a horizon layer anchored to the
// bottom and sized from --hero-land-h. Mixing % and landscape-relative stops
// in one gradient makes them collide on short heroes (a hard colour edge);
// two layers blend smoothly at any size.

// Day: soft morning blue to a pale mint horizon, with a warm wash spilling
// from the sun and a deeper blue in the far corner for depth.
.sky-fill--day {
  background:
    radial-gradient(52% 58% at 86% 13%, rgba(255, 248, 226, 0.9) 0%, rgba(255, 243, 214, 0.42) 32%, rgba(255, 243, 214, 0) 72%),
    radial-gradient(70% 60% at 4% 0%, rgba(150, 200, 230, 0.42) 0%, rgba(150, 200, 230, 0) 70%),
    linear-gradient(0deg, #f4f7e6 0%, #eaf5ec 42%, rgba(234, 245, 236, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 1.9) no-repeat,
    linear-gradient(180deg, #bfe0f0 0%, #d9eef0 40%, #e3f2ee 100%);
}

// Night: ink blue to a deep green horizon, lifted around the moon. It leads
// the change both ways (front-loaded), so the sky never lags the text.
.sky-fill--night {
  background:
    radial-gradient(48% 52% at 86% 14%, rgba(120, 150, 215, 0.2) 0%, rgba(120, 150, 215, 0.06) 45%, rgba(120, 150, 215, 0) 75%),
    radial-gradient(60% 40% at 20% 100%, rgba(30, 90, 70, 0.18) 0%, rgba(30, 90, 70, 0) 70%),
    linear-gradient(0deg, #0c2418 0%, #0b1d1d 40%, rgba(11, 29, 29, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 1.9) no-repeat,
    linear-gradient(180deg, #040a12 0%, #081522 42%, #0a1a22 100%);
  opacity: 1;
  transition: opacity 1.15s cubic-bezier(0.4, 0, 0.3, 1) 0.15s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.8s $ease-front;
  }
}

// Twilight skies. Each is outer x inner opacity; at rest one factor is 0 in
// either theme, so they never show on load or at rest. Going its own way the
// outer factor rises fast and the inner one falls late (a bloom); going the
// other way the outer one drops at once, so dusk never plays at sunrise and
// dawn never plays at sunset.
// Dusk: indigo overhead, violet, rose, then a glowing orange horizon.
.sky-tw-fill--dusk {
  opacity: 0.94;
  background:
    radial-gradient(46% 52% at 86% 34%, rgba(255, 168, 104, 0.6) 0%, rgba(255, 140, 96, 0.22) 40%, rgba(255, 140, 96, 0) 72%),
    linear-gradient(0deg, #ffc27c 0%, #f79a5e 36%, #e9806a 50%, rgba(214, 104, 124, 0.85) 64%, rgba(150, 82, 144, 0.4) 82%, rgba(150, 82, 144, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 2.2) no-repeat,
    linear-gradient(180deg, #1a2350 0%, #2f3473 26%, #54448a 58%, #7a4d8e 100%);
}

// Dawn: paler and softer, periwinkle and lavender over peach and gold.
.sky-tw-fill--dawn {
  opacity: 0.92;
  background:
    radial-gradient(46% 52% at 86% 30%, rgba(255, 226, 170, 0.75) 0%, rgba(255, 206, 160, 0.28) 40%, rgba(255, 206, 160, 0) 72%),
    linear-gradient(0deg, #ffe6b6 0%, #ffd6a2 36%, rgba(252, 200, 168, 0.95) 50%, rgba(240, 186, 186, 0.7) 66%, rgba(214, 178, 210, 0.3) 84%, rgba(214, 178, 210, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 2.2) no-repeat,
    linear-gradient(180deg, #8095cc 0%, #a3a4d5 32%, #c4b0d4 70%, #d8b8cc 100%);
}

// Dusk: rest dark = 1 x 0, rest light = 0 x 1.
.sky-tw--dusk {
  opacity: 1;
  transition: opacity 0.45s $ease-soft;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.25s ease-in;
  }
}

.sky-tw--dusk > .sky-tw-in {
  opacity: 0;
  transition: opacity 0.7s $ease-io 1.25s;

  #{$light} & {
    opacity: 1;
    transition: opacity 0s linear 0.3s;
  }
}

// Dawn: rest light = 1 x 0, rest dark = 0 x 1.
.sky-tw--dawn {
  opacity: 0;
  transition: opacity 0.25s ease-in;

  #{$light} & {
    opacity: 1;
    transition: opacity 0.4s $ease-soft;
  }
}

.sky-tw--dawn > .sky-tw-in {
  opacity: 1;
  transition: opacity 0s linear 0.3s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.95s $ease-io 0.55s;
  }
}

@media (max-width: 575px) {
  .sky-fill--day {
    background:
      radial-gradient(70% 40% at 88% 8%, rgba(255, 248, 226, 0.9) 0%, rgba(255, 243, 214, 0.4) 34%, rgba(255, 243, 214, 0) 72%),
      linear-gradient(0deg, #f4f7e6 0%, #eaf5ec 42%, rgba(234, 245, 236, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 1.9) no-repeat,
      linear-gradient(180deg, #bfe0f0 0%, #d9eef0 40%, #e3f2ee 100%);
  }

  .sky-fill--night {
    background:
      radial-gradient(70% 34% at 86% 8%, rgba(120, 150, 215, 0.2) 0%, rgba(120, 150, 215, 0.06) 45%, rgba(120, 150, 215, 0) 75%),
      linear-gradient(0deg, #0c2418 0%, #0b1d1d 40%, rgba(11, 29, 29, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 1.9) no-repeat,
      linear-gradient(180deg, #040a12 0%, #081522 42%, #0a1a22 100%);
  }

  .sky-tw-fill--dusk {
    background:
      radial-gradient(70% 34% at 88% 14%, rgba(255, 168, 104, 0.6) 0%, rgba(255, 140, 96, 0.22) 40%, rgba(255, 140, 96, 0) 72%),
      linear-gradient(0deg, #ffc27c 0%, #f79a5e 36%, #e9806a 50%, rgba(214, 104, 124, 0.85) 64%, rgba(150, 82, 144, 0.4) 82%, rgba(150, 82, 144, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 2.2) no-repeat,
      linear-gradient(180deg, #1a2350 0%, #2f3473 26%, #54448a 58%, #7a4d8e 100%);
  }

  .sky-tw-fill--dawn {
    background:
      radial-gradient(70% 34% at 88% 12%, rgba(255, 226, 170, 0.75) 0%, rgba(255, 206, 160, 0.28) 40%, rgba(255, 206, 160, 0) 72%),
      linear-gradient(0deg, #ffe6b6 0%, #ffd6a2 36%, rgba(252, 200, 168, 0.95) 50%, rgba(240, 186, 186, 0.7) 66%, rgba(214, 178, 210, 0.3) 84%, rgba(214, 178, 210, 0) 100%) left bottom / 100% calc(var(--hero-land-h) * 2.2) no-repeat,
      linear-gradient(180deg, #8095cc 0%, #a3a4d5 32%, #c4b0d4 70%, #d8b8cc 100%);
  }
}

// A whisper of noise that dithers the long, dark gradient (no banding on
// big monitors).
.sky-grain {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");
  opacity: 0.05;
  transition: opacity 1.2s ease 0.3s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.4s ease;
  }
}

// Horizon haze just above where the mountains start.
.sky-haze {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(var(--hero-land-h) * 0.28);
  height: calc(var(--hero-land-h) * 1.2);
}

.sky-haze--day {
  background: linear-gradient(0deg, rgba(255, 246, 222, 0) 0%, rgba(255, 243, 212, 0.72) 32%, rgba(255, 247, 230, 0.3) 64%, rgba(255, 250, 238, 0) 100%);
  opacity: 0;
  transition: opacity 0.6s ease-in;

  #{$light} & {
    opacity: 1;
    transition: opacity 1s $ease-front 0.3s;
  }
}

.sky-haze--night {
  background: linear-gradient(0deg, rgba(34, 96, 74, 0) 0%, rgba(40, 112, 88, 0.16) 34%, rgba(30, 84, 92, 0.06) 68%, rgba(30, 84, 92, 0) 100%);
  opacity: 1;
  transition: opacity 1.2s $ease-io 0.5s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.5s ease-in;
  }
}

// Sunset / sunrise afterglow along the horizon, over the night fill so it
// lingers as the sky darkens. Steady states: dark = 0 x 1, light = 1 x 0.
.sky-dusk {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: calc(var(--hero-land-h) * 0.25);
  opacity: 0;
  transition: opacity 1.4s $ease-io 0.55s;

  #{$light} & {
    opacity: 1;
    transition: opacity 0.45s ease-out;
  }
}

.sky-dusk-glow {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background:
    radial-gradient(60% 46% at 86% 62%, rgba(255, 160, 100, 0.3) 0%, rgba(255, 160, 100, 0) 70%),
    linear-gradient(0deg, rgba(255, 128, 72, 0) 0%, rgba(255, 138, 82, 0.5) 12%, rgba(252, 150, 110, 0.3) 26%, rgba(222, 136, 160, 0.12) 46%, rgba(150, 130, 200, 0) 70%);
  opacity: 1;
  transition: opacity 0.5s ease-out;

  #{$light} & {
    opacity: 0;
    transition: opacity 1.6s $ease-io 0.45s;
  }
}

.sky-wake {
  opacity: 0;
  visibility: hidden;
  background: linear-gradient(180deg, rgba(1, 4, 9, 0.82) 0%, rgba(3, 9, 14, 0.6) 100%);

  #{$light} & {
    background: linear-gradient(180deg, rgba(178, 168, 210, 0.46) 0%, rgba(255, 200, 168, 0.5) 62%, rgba(255, 214, 172, 0.6) 100%);
  }
}

/* ---------------------------------------------------------------------- */
/* Far layer: aurora, stars, shooting star, sun and moon                  */
/* ---------------------------------------------------------------------- */

.sky-far {
  position: absolute;
  top: -12px;
  bottom: -12px;
  left: -16px;
  right: -16px;
  transform: translate3d(calc(var(--hero-px) * 4px), calc(var(--hero-py) * 3px), 0);
}

.sky-aurora {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 1;
  transition: opacity 2s $ease-io 0.9s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.7s ease-in;
  }
}

.aurora-ribbon {
  position: absolute;
  height: clamp(130px, 14vw + 70px, 320px);
}

.aurora-wave {
  width: 100%;
  height: 100%;
  transform-origin: 50% 100%;
  animation: sky-aurora-a 26s ease-in-out infinite alternate;
}

.aurora-wave--b {
  animation-name: sky-aurora-b;
}

.aurora-breathe {
  width: 100%;
  height: 100%;
  animation: sky-aurora-breathe 12s ease-in-out infinite alternate;
}

.aurora-svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
  filter: blur(3.5px);
}

.sky-stars {
  position: absolute;
  top: 1.5%;
  left: 0;
  right: 0;
  bottom: calc(var(--hero-land-h) * 0.8);
}

.sky-deep {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 1;
  transition: opacity 1.5s $ease-io 0.5s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.5s ease-in;
  }
}

// The Milky Way: a barely-there glow, tapered at both ends.
.sky-band {
  position: absolute;
  transform-origin: 50% 50%;
  background: linear-gradient(180deg, rgba(170, 190, 255, 0) 0%, rgba(176, 194, 255, 0.05) 30%, rgba(200, 210, 255, 0.085) 50%, rgba(176, 194, 255, 0.05) 70%, rgba(170, 190, 255, 0) 100%);
  -webkit-mask-image: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, #000 28%, #000 66%, rgba(0, 0, 0, 0) 100%);
  mask-image: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, #000 28%, #000 66%, rgba(0, 0, 0, 0) 100%);
}

.sky-dust {
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  fill: #dfe8ff;
}

.star {
  position: absolute;
  opacity: var(--o);
  transition: opacity 1.1s ease-out calc(0.45s + var(--d));

  #{$light} & {
    opacity: 0;
    transition: opacity 0.55s ease-in var(--d2);
  }
}

.star-dot {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.tw-a {
  animation: sky-twinkle-a 4s ease-in-out infinite;
}

.tw-b {
  animation: sky-twinkle-b 5s ease-in-out infinite;
}

// The brightest few: a soft four-point glint that slowly swells.
.tw-g {
  animation: sky-glint 6s ease-in-out infinite;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    width: calc(var(--s) * 7);
    height: 1px;
    margin-left: calc(var(--s) * -3.5);
    margin-top: -0.5px;
    border-radius: 1px;
    background: linear-gradient(90deg, rgba(220, 232, 255, 0) 0%, rgba(236, 242, 255, 0.85) 50%, rgba(220, 232, 255, 0) 100%);
  }

  &::after {
    transform: rotate(90deg);
  }
}

// Off-theme layers stop ticking: no twinkle or aurora loops by day, no
// turning rays or breathing sun glow by night.
#{$light} .star-dot,
#{$light} .aurora-wave,
#{$light} .aurora-breathe,
#{$dark} .sun-rays,
#{$dark} .sun-glow {
  animation-play-state: paused;
}

.meteor {
  position: absolute;
  left: 50%;
  top: 10%;
  width: 0;
  height: 0;
  opacity: 1;
  // Only once the sky is dark enough (no shooting stars in a twilight sky).
  transition: opacity 0.6s ease 1.2s;
  --mt: 300px;
  --ml: 120px;
  --md: 0.9s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.4s ease;
  }
}

// The mover is as wide as the path, so translating it by 100% of itself
// carries the head the whole way (no custom properties inside keyframes).
.meteor-move {
  position: absolute;
  left: 0;
  top: 0;
  width: var(--mt);
  height: 0;
  opacity: 0;
}

.meteor-tail {
  position: absolute;
  right: 100%;
  top: -0.75px;
  width: var(--ml);
  height: 1.5px;
  border-radius: 1px;
  background: linear-gradient(90deg, rgba(200, 220, 255, 0) 0%, rgba(214, 228, 255, 0.32) 55%, rgba(255, 255, 255, 0.95) 100%);
  transform-origin: 100% 50%;
  transform: scaleX(0.05);
}

.meteor-head {
  position: absolute;
  left: -1.5px;
  top: -1.5px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 6px 2px rgba(214, 228, 255, 0.85), 0 0 16px 5px rgba(160, 190, 255, 0.3);
}

.meteor.is-flying .meteor-move {
  animation: sky-meteor 0.9s linear both;
  animation-duration: var(--md);
}

.meteor.is-flying .meteor-tail {
  animation: sky-meteor-tail 0.9s cubic-bezier(0.3, 0.6, 0.4, 1) both;
  animation-duration: var(--md);
}

// Sun and moon share one anchor in the upper right.
.sky-orb {
  position: absolute;
  top: 9%;
  right: 13%;
  width: clamp(64px, 7vw, 110px);
  height: clamp(64px, 7vw, 110px);
}

@media (max-width: 575px) {
  .sky-orb {
    top: 5%;
    right: 8%;
  }
}

// Small phones: a smaller orb tucked in the corner, clear of the title.
@media (max-width: 420px) {
  .sky-orb {
    top: 3%;
    right: 8%;
    width: clamp(44px, 13vw, 64px);
    height: clamp(44px, 13vw, 64px);
  }
}

// Phones held sideways: a hero barely taller than the title row, so a small
// orb high in the corner, and a shorter sunset that never sweeps the title.
// (Mirrored by LAND_PHONE in geometry().)
@media (max-height: 520px) and (orientation: landscape) {
  .hero-sky {
    --sky-set: 72px;
  }

  .sky-orb {
    top: 6%;
    right: 5%;
    width: 46px;
    height: 46px;
  }
}

.sun,
.moon {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

// Sunset (into dark): it warms up and sinks a long way while still bright,
// then the disc melts into its glow, which fades last. Sunrise (into light):
// it rises once the moon has set, still warm. The two share an anchor, so
// each waits for the other.
.sun {
  transform: translate3d(18px, var(--sky-set), 0) scale(0.9);
  transition: transform 1.2s $ease-sink;

  #{$light} & {
    transform: none;
    transition: transform 1.6s $ease-out 0.75s;
  }
}

.sun-halo,
.sun-core {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sun-core {
  opacity: 0;
  transition: opacity 0.32s ease-in 0.8s;

  #{$light} & {
    opacity: 1;
    transition: opacity 0.8s ease-out 0.8s;
  }
}

// The glare is a ring around the disc: it travels with the disc (the .sun
// transform transition, restated because `transition` is one property) and
// fades with it, or it would outline an empty circle (an eclipse) off the
// disc or once the disc has gone.
.sun.sun-glare {
  opacity: 0;
  transition: transform 1.2s $ease-sink, opacity 0.32s ease-in 0.8s;

  #{$light} & {
    opacity: 1;
    transition: transform 1.6s $ease-out 0.75s, opacity 0.8s ease-out 0.8s;
  }
}

.sun-halo {
  opacity: 0;
  transition: opacity 0.5s ease-in 0.8s;

  #{$light} & {
    opacity: 1;
    transition: opacity 0.9s ease-out 0.7s;
  }
}

.sun-rays,
.sun-glow,
.sun-glow-warm,
.moon-halo {
  position: absolute;
  left: 50%;
  top: 50%;
}

// Long, very faint light rays turning almost imperceptibly. It is a large
// masked surface, so it turns in small steps (0.5deg every 1/3 s, too soft
// to see as a tick) instead of being redrawn every frame, and holds still
// on touch screens.
.sun-rays {
  width: 720%;
  height: 720%;
  margin: -360% 0 0 -360%;
  background:
    repeating-conic-gradient(from 4deg, rgba(255, 240, 200, 0) 0deg, rgba(255, 240, 200, 0.6) 3deg, rgba(255, 240, 200, 0) 7deg, rgba(255, 240, 200, 0) 17deg),
    repeating-conic-gradient(from 11deg, rgba(255, 236, 190, 0) 0deg, rgba(255, 236, 190, 0.45) 5deg, rgba(255, 236, 190, 0) 11deg, rgba(255, 236, 190, 0) 29deg);
  -webkit-mask-image: radial-gradient(closest-side, #000 6%, rgba(0, 0, 0, 0.5) 30%, rgba(0, 0, 0, 0) 100%);
  mask-image: radial-gradient(closest-side, #000 6%, rgba(0, 0, 0, 0.5) 30%, rgba(0, 0, 0, 0) 100%);
  opacity: 0.26;
  animation: sky-spin 240s steps(720) infinite;
}

@media (hover: none) {
  .sun-rays {
    animation: none;
  }
}

.sun-bloom,
.moon-bloom {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sun-glow,
.sun-glow-warm {
  width: 520%;
  height: 520%;
  margin: -260% 0 0 -260%;
  border-radius: 50%;
}

.sun-glow {
  background: radial-gradient(closest-side, rgba(255, 246, 212, 1) 0%, rgba(255, 226, 140, 0.76) 12%, rgba(255, 206, 104, 0.42) 25%, rgba(255, 206, 110, 0.16) 47%, rgba(255, 210, 120, 0.05) 72%, rgba(255, 210, 120, 0) 100%);
  animation: sky-breathe 7s ease-in-out infinite alternate;
}

.sun-glow-warm {
  background: radial-gradient(closest-side, rgba(255, 196, 130, 0.9) 0%, rgba(255, 146, 84, 0.55) 16%, rgba(255, 120, 72, 0.2) 40%, rgba(255, 120, 72, 0) 78%);
}

.sun-disc,
.sun-corona,
.sun-tint {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

// A white-hot core easing into warm gold at the limb...
.sun-disc {
  background: radial-gradient(circle at 46% 44%, #fffffb 0%, #fffae6 40%, #fff0c0 72%, #ffe08a 100%);
}

// ...ringed by a tight, bright corona (an outer shadow only: it is never
// painted under the disc).
.sun-corona {
  box-shadow: 0 0 14px 3px rgba(255, 240, 176, 0.95), 0 0 44px 14px rgba(255, 208, 106, 0.55), 0 0 100px 34px rgba(255, 200, 100, 0.22);
}

// The setting sun: still brightest at its heart (a pale corona around a
// darker orange disc would read as an eclipse).
.sun-tint {
  background: radial-gradient(circle at 44% 42%, #fff6e0 0%, #ffdba6 38%, #ffbb76 78%, #ff9c58 100%);
  box-shadow: 0 0 12px 3px rgba(255, 196, 120, 0.85), 0 0 40px 12px rgba(255, 150, 80, 0.45), 0 0 90px 30px rgba(255, 130, 70, 0.18);
}

// Warm tint: on at night (where the sun is hidden), so setting turns it
// orange fast, and rising starts orange and pales once it is up. The white
// corona does the opposite.
.sun-tint,
.sun-glow-warm {
  opacity: 1;
  transition: opacity 0.6s ease-out;

  #{$light} & {
    opacity: 0;
    transition: opacity 1.4s $ease-io 1.15s;
  }
}

.sun-corona {
  opacity: 0;
  transition: opacity 0.6s ease-out;

  #{$light} & {
    opacity: 1;
    transition: opacity 1.4s $ease-io 1.15s;
  }
}

@media (max-width: 420px) {
  .sun-glow,
  .sun-glow-warm {
    width: 440%;
    height: 440%;
    margin: -220% 0 0 -220%;
  }
}

// Moonrise (into dark) follows the sunset; moonset (into light) leads, and
// travels visibly before it fades.
.moon {
  opacity: 1;
  transform: none;
  transition: transform 1.7s $ease-out 1.12s, opacity 0.9s ease-out 1.12s;

  #{$light} & {
    opacity: 0;
    transform: translate3d(-18px, 70px, 0) scale(0.9);
    transition: transform 0.9s $ease-sink, opacity 0.4s ease-in 0.45s;
  }
}

.moon-bloom {
  transform: none;
  transition: transform 2.2s $ease-out 1.12s;

  #{$light} & {
    transform: scale(0.5);
    transition: transform 0.9s ease-in;
  }
}

.moon-halo {
  width: 460%;
  height: 460%;
  margin: -230% 0 0 -230%;
  border-radius: 50%;
  background: radial-gradient(closest-side, rgba(226, 236, 255, 0.55) 0%, rgba(196, 214, 255, 0.3) 17%, rgba(176, 200, 252, 0.13) 38%, rgba(166, 192, 250, 0.04) 64%, rgba(166, 192, 250, 0) 100%);
  animation: sky-breathe 9s ease-in-out infinite alternate;
}

@media (max-width: 420px) {
  .moon-halo {
    width: 380%;
    height: 380%;
    margin: -190% 0 0 -190%;
  }
}

.sun-glare,
.moon-glare {
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
  }
}

// Both glares are clear over the disc itself (the disc ends at 33% of the
// sun glare's radius and 24% of the moon glare's): they only light the
// clouds around it, never wash out the orb.
.sun-glare::before {
  width: 300%;
  height: 300%;
  margin: -150% 0 0 -150%;
  background: radial-gradient(closest-side, rgba(255, 250, 228, 0) 0%, rgba(255, 250, 228, 0) 31%, rgba(255, 248, 222, 0.42) 35%, rgba(255, 244, 210, 0.22) 50%, rgba(255, 236, 190, 0.07) 72%, rgba(255, 236, 190, 0) 100%);
}

.moon-glare::before {
  width: 320%;
  height: 320%;
  margin: -160% 0 0 -160%;
  background: radial-gradient(closest-side, rgba(232, 240, 255, 0) 0%, rgba(232, 240, 255, 0) 23.5%, rgba(226, 236, 255, 0.16) 25.5%, rgba(206, 220, 255, 0.09) 38%, rgba(196, 212, 255, 0.03) 62%, rgba(196, 212, 255, 0) 100%);
}

.moon-svg {
  position: absolute;
  top: 4%;
  left: 4%;
  width: 92%;
  height: 92%;
  overflow: visible;
}

.moon-maria {
  fill: #c3cce3;
  opacity: 0.62;
}

.moon-maria--deep {
  fill: #b2bdd7;
  opacity: 0.45;
}

.moon-crater-rim {
  fill: none;
  stroke: #ffffff;
  stroke-opacity: 0.62;
  stroke-linecap: round;
}

.moon-bright {
  fill: #ffffff;
  opacity: 0.9;
}

// (Its stroke is a gradient set on the element.)
.moon-rim {
  fill: none;
  stroke-width: 0.9;
}

/* ---------------------------------------------------------------------- */
/* Clouds                                                                 */
/* ---------------------------------------------------------------------- */

.sky-clouds {
  position: absolute;
  top: -20px;
  bottom: -20px;
  left: -24px;
  right: -24px;
  transform: translate3d(calc(var(--hero-px) * 8px), calc(var(--hero-py) * 4px), 0);
}

// A zero-height lane as wide as the hero plus the cloud: translating it by
// 100% of itself carries the cloud from fully off the left edge to fully off
// the right one, whatever the screen width. Clouds thin out while they cross
// the centre column, where the title sits: an opacity step in the drift
// itself (compositor-only), not a mask over the whole moving layer.
.cloud-lane {
  position: absolute;
  left: calc(-1 * (var(--cw) + 28px));
  right: -28px;
  height: 0;
  animation: sky-drift 120s linear infinite;
}

.cloud-in {
  position: absolute;
  left: 0;
  top: 0;
}

.cloud-day,
.cloud-night {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

// Day: a puffy cumulus. Night: the same lane carries a smaller, translucent
// moonlit veil; the two crossfade, so clouds thin out as night falls.
.cloud-day {
  opacity: 0;
  transition: opacity 0.9s $ease-io 0.05s;

  #{$light} & {
    opacity: var(--od);
    transition: opacity 1.1s $ease-io 0.25s;
  }
}

.cloud-night {
  opacity: var(--on);
  transform: scale(0.76);
  transform-origin: 50% 58%;
  transition: opacity 1.4s $ease-io 0.4s;

  #{$light} & {
    opacity: 0;
    transition: opacity 0.6s ease-in;
  }
}

.cloud-svg {
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;

  &.is-flipped {
    transform: scaleX(-1);
  }
}

/* ---------------------------------------------------------------------- */
/* Entrance (only while .is-entering; see ENTRANCE_MS)                    */
/* ---------------------------------------------------------------------- */

.is-entering {
  .sky-wake {
    animation: sky-wake 2s cubic-bezier(0.33, 0, 0.2, 1) 0.05s backwards;
  }

  .sky-haze {
    animation: sky-fade-in 1.6s ease-out 0.2s backwards;
  }

  .sky-aurora {
    animation: sky-fade-in 1.6s ease-out 1s backwards;
  }

  .sky-deep {
    animation: sky-fade-in 2s ease-out 0.3s backwards;
  }

  // No `to` keyframe: it ends on the current value, so in light mode the
  // entrance stays invisible.
  .star {
    animation: sky-star-in 0.9s $ease-soft var(--e) backwards;
  }

  .sky-orb {
    animation: sky-orb-rise 1.8s $ease-rise 0.05s backwards;
  }

  .sun-bloom,
  .moon-bloom {
    animation: sky-bloom 2.1s $ease-out 0.1s backwards;
  }

  .cloud-in {
    animation: sky-cloud-in 2.2s $ease-soft backwards;
  }
}

/* ---------------------------------------------------------------------- */
/* Reduced motion: a still, complete sky                                  */
/* ---------------------------------------------------------------------- */

@mixin still-sky {
  .sky-orb,
  .sun-bloom,
  .moon-bloom,
  .sun-glow,
  .moon-halo,
  .sun-rays,
  .sky-haze,
  .sky-aurora,
  .sky-deep,
  .aurora-wave,
  .aurora-breathe,
  .star,
  .star-dot,
  .cloud-lane,
  .cloud-in {
    animation: none !important;
  }

  .sky-wake,
  .sky-tw,
  .sky-dusk,
  .meteor {
    display: none;
  }

  .sun,
  .moon,
  .moon-bloom {
    transform: none !important;
  }

  * {
    transition-duration: 0.6s !important;
    transition-delay: 0s !important;
  }
}

.is-still {
  @include still-sky;
}

@media (prefers-reduced-motion: reduce) {
  .hero-sky {
    @include still-sky;
  }
}

/* ---------------------------------------------------------------------- */
/* Keyframes                                                              */
/* ---------------------------------------------------------------------- */

@keyframes sky-wake {
  from {
    opacity: 1;
    visibility: visible;
  }

  to {
    opacity: 0;
    visibility: hidden;
  }
}

// Entrance keyframes leave out `to`, so each ends exactly on the element's
// own (theme-dependent) resting value.
@keyframes sky-fade-in {
  from {
    opacity: 0;
  }
}

@keyframes sky-orb-rise {
  from {
    opacity: 0;
    transform: translate3d(0, 38px, 0) scale(0.92);
  }
}

@keyframes sky-bloom {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }

  55% {
    opacity: 1;
    transform: scale(1.22);
  }
}

@keyframes sky-star-in {
  0% {
    opacity: 0;
    transform: scale(0.2);
  }

  60% {
    transform: scale(1.35);
  }
}

@keyframes sky-cloud-in {
  from {
    opacity: 0;
    transform: translate3d(-48px, 0, 0);
  }
}

@keyframes sky-breathe {
  from {
    opacity: 0.84;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1.06);
  }
}

@keyframes sky-spin {
  to {
    transform: rotate(360deg);
  }
}

// The opacity dip spans the cloud's centre crossing the middle of the sky
// (22-78% of its width, flat over 40-60%): about 27-73% of the crossing at
// every width and cloud size. cloudThin() in the script mirrors it.
@keyframes sky-drift {
  0% {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }

  27% {
    opacity: 1;
  }

  42% {
    opacity: 0.42;
  }

  58% {
    opacity: 0.42;
  }

  73% {
    opacity: 1;
  }

  100% {
    transform: translate3d(100%, 0, 0);
    opacity: 1;
  }
}

@keyframes sky-twinkle-a {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

@keyframes sky-twinkle-b {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  44% {
    opacity: 0.9;
    transform: scale(1);
  }

  52% {
    opacity: 0.28;
    transform: scale(0.7);
  }

  62% {
    opacity: 0.95;
    transform: scale(1.12);
  }

  72% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes sky-glint {
  0%,
  100% {
    opacity: 0.66;
    transform: scale(0.75) rotate(0deg);
  }

  50% {
    opacity: 1;
    transform: scale(1.12) rotate(12deg);
  }
}

@keyframes sky-meteor {
  0% {
    opacity: 0;
    transform: translate3d(0, 0, 0);
  }

  12% {
    opacity: 1;
  }

  66% {
    opacity: 0.95;
  }

  100% {
    opacity: 0;
    transform: translate3d(100%, 0, 0);
  }
}

@keyframes sky-meteor-tail {
  0% {
    transform: scaleX(0.05);
  }

  55% {
    transform: scaleX(1);
  }

  100% {
    transform: scaleX(0.35);
  }
}

@keyframes sky-aurora-a {
  0% {
    transform: translate3d(-3%, 0, 0) skewX(-6deg) scaleY(0.92);
  }

  50% {
    transform: translate3d(1%, 2%, 0) skewX(2deg) scaleY(1.06);
  }

  100% {
    transform: translate3d(4%, -1%, 0) skewX(7deg) scaleY(0.97);
  }
}

@keyframes sky-aurora-b {
  0% {
    transform: translate3d(3%, 1%, 0) skewX(5deg) scaleY(1.04);
  }

  50% {
    transform: translate3d(-1%, -1%, 0) skewX(-3deg) scaleY(0.93);
  }

  100% {
    transform: translate3d(-4%, 1%, 0) skewX(-7deg) scaleY(1.08);
  }
}

@keyframes sky-aurora-breathe {
  from {
    opacity: 0.55;
  }

  to {
    opacity: 1;
  }
}
</style>
